/**
 * Central API client for the self-hosted Node/Express backend.
 * Base URL comes from VITE_PUBLIC_API_URL (defaults to /api).
 * Attaches the JWT from localStorage; on 401 clears the session.
 */
const API_BASE: string = (import.meta.env.VITE_PUBLIC_API_URL as string) || "/api";

const TOKEN_KEY = "nomad_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data: T;
}

// Minimal fetch options type (avoids dependency on DOM lib globals).
type FetchOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
};

async function request<T = unknown>(
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  let body: ApiResponse<T> | null = null;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    body = null;
  }

  if (res.status === 401) {
    clearToken();
  }

  if (!res.ok || !body?.success) {
    const message = body?.message || `Request failed (${res.status})`;
    throw new ApiError(message, res.status, body?.data ?? null);
  }

  return body.data;
}

export class ApiError extends Error {
  status: number;
  details: unknown;
  constructor(message: string, status: number, details: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export const api = {
  get: <T = unknown>(path: string) => request<T>(path),
  post: <T = unknown>(path: string, data: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(data ?? {}) }),
  put: <T = unknown>(path: string, data: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data ?? {}) }),
  patch: <T = unknown>(path: string, data: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(data ?? {}) }),
  del: <T = unknown>(path: string) => request<T>(path, { method: "DELETE" }),
};